import { Component, ErrorHandler, Input, OnInit, ViewChild } from '@angular/core';

import { AuthService } from '../../auth/auth/auth.service';
import { IAuthEvent } from '../../auth/models/auth-event.model';
import { UserService } from '../user/user.service';
import { UserInfo } from '../models/user-info.model';
import { State } from '../state-view/state.model';
import { ActionIconComponent } from '../action-icon/action-icon.component';
import { MatSnackBar } from '@angular/material';

@Component({
  selector: 'app-event-favorite-icon',
  templateUrl: './event-favorite-icon.component.html',
  styleUrls: ['./event-favorite-icon.component.scss']
})
export class EventFavoriteIconComponent implements OnInit {
  @Input() eventId: number;
  @ViewChild('iconCmp') iconCmp: ActionIconComponent;
  authenticated = false;
  state = State.Empty;
  private _userInfo: UserInfo;

  constructor(private _auth: AuthService,
              private _userService: UserService,
              private _errorHandler: ErrorHandler,
              private _snackBar: MatSnackBar) { }

  ngOnInit() {
    // Update state based on latest event in authStream, and subscribe to any future
    // changes in authentication state.
    this._onAuthEvent(this._auth.authStream.getValue());
    this._auth.authStream.subscribe(e => this._onAuthEvent(e));

    // Update state based on latest available userInfo, and subscribe to any future
    // changes to userInfo.
    this._userInfo = this._userService.userInfo.getValue();
    this._userService.userInfo.subscribe(u => {
      // Update userInfo
      this._userInfo = u;
      // Set state to loaded
      this.state = State.Content;
    });
  }

  get isFavorite(): boolean {
    return this._userInfo && this._userInfo.favoriteEvents.some(f => f.eventId === this.eventId);
  }

  get tooltip(): string {
    return this.isFavorite
      ? 'This event is in your favorites (click to remove)'
      : 'Add to favorites';
  }

  get icon(): string {
    return this.isFavorite ? 'favorite' : 'favorite_border';
  }

  private _onAuthEvent(e: IAuthEvent) {
    this.authenticated = this._auth.authenticated;
    if (this.authenticated && !this._auth.endUser) {
      // userInfo is not available when not authenticated as an end user, so set favorite state to loaded now.
      this.state = State.Content;
    }
  }

  async toggleFavorite(v) {
    if (!this._auth.authenticated || !this._auth.endUser) {
      this._auth.redirectToLogin();
      return;
    }
    this.state = State.Loading;
    const snackBarOpts = { duration: 2000 };
    try {
      if (v) {
        await this._userService.removeFavoriteEvent(this.eventId);
        this._snackBar.open(`The event was removed from your favorites.`, 'Dismiss', snackBarOpts);
      } else {
        await this._userService.addFavoriteEvent(this.eventId);
        this._snackBar.open(`The event was added to your favorites.`, 'Dismiss', snackBarOpts);
      }
    } catch (e) {
      this._snackBar.open('An error occurred updating your event favorites. Please try again later.', 'Dismiss', snackBarOpts);
      this._errorHandler.handleError(e);

      // Set state to error, then reset after a few seconds to allow retry.
      this.state = State.Error;
      setTimeout(() => {
        this.state = State.Content;
      }, 2000);
    }
  }
}
